import type { PayloadAction } from "@reduxjs/toolkit";
import { createSelector, createSlice } from "@reduxjs/toolkit";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import type { AuthResponse, Resource, Role, User } from "@types";
import { getApiUrl, safeConsole } from "@utils";
import { signOut } from "firebase/auth";
import { useCallback, useEffect } from "react";
import { teardownNativeCallKit } from "@/mobile/callKit";
import { getFirebaseAuth, isFirebaseReady } from "../../firebase";
import {
  canAccessRoute,
  findMatchingRoute,
  isPublicRoute,
} from "../../router/routeConfig";
import { rememberImpersonationToken } from "../auth/impersonationToken";
import { currentUser, permissionsForUser } from "../auth/permissions";
import { authPathForCompanyUrl } from "../hooks/companyPath";
import { revokeRegisteredDispatchPushDevice } from "../mobile/pushNotifications";
// Direct import (not the @services barrel): the barrel also exports apiClient,
// which imports the silent-auth retry interceptor, which imports this module —
// importing the barrel here would close an import cycle.
import { queryKeys } from "../services/queryKeys";
import { useAppDispatch, useAppSelector } from "./hooks";
import {
  isAuthSyncStorageKey,
  markIntentionalLogout,
  normalizeSessionUser,
  type PersistedAuthSnapshot,
  readPersistedAccessToken,
  readPersistedAuth,
  readPersistedCompanyId,
  readPersistedSession,
} from "./sessionPersistence";
import type { RootState } from "./store";

export {
  accessTokenNeedsRefresh,
  clearIntentionalLogoutMarker,
  getStoredSessionUser,
  wasIntentionalLogout,
} from "./sessionPersistence";

/**
 * An open staff impersonation session (SD-16244).
 *
 * Held ALONGSIDE the staff credentials in `SessionState`, never replacing
 * them: the staff token stays available for session management, and — because
 * only `accessToken` is persisted and pushed to the phone extension — an
 * impersonation token can never reach storage, another tab, or the softphone.
 */
export type ImpersonationSession = {
  /** Bearer for the viewed-as surface. Deliberately memory-only. */
  token: string;
  /** Ledger id; echoed by the backend on every impersonated response. */
  sessionId: string;
  actorId: string;
  subjectId: string;
  /** Human label for the banner; an id tells the actor nothing. */
  subjectLabel?: string;
  mode: string;
  /** ISO-8601 UTC. */
  expiresAt: string;
  /** Staff identity to restore on exit, so leaving is not a re-login. */
  staffUser?: User;
  staffCompanyId?: string;
};

export type SessionState = {
  /**
   * The signed-in staff/own token. Impersonation never overwrites it — read
   * `staffAccessToken` for session management and `accessToken` (the effective
   * selector) for ordinary app requests.
   */
  accessToken: string;
  companyId?: string;
  user?: User;
  impersonation?: ImpersonationSession;
};

const initialState: SessionState = readPersistedSession();

export const sessionSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
    hydrate: (state, action: PayloadAction<SessionState>) => {
      state.accessToken = action.payload.accessToken;
      state.companyId = action.payload.companyId;
      state.user = action.payload.user;
    },
    login: (state, action: PayloadAction<AuthResponse>) => {
      const user = normalizeSessionUser(action.payload.user);
      state.companyId = user.companyIds[0];
      state.accessToken = action.payload.accessToken;
      state.user = user;
    },
    /**
     * Apply the token + user snapshot read after ANOTHER tab changed storage
     * (silent re-mint or logout). Unlike `hydrate`, deliberately leaves
     * `companyId` alone: adopting another tab's company switch here would
     * change this tab's tenant without the query-cache invalidation that
     * `setCompanyId` performs.
     */
    syncAuthFromStorage: (
      state,
      action: PayloadAction<PersistedAuthSnapshot>,
    ) => {
      state.accessToken = action.payload.accessToken;
      state.user = action.payload.user;
    },
    /**
     * Apply a silently re-minted token WITHOUT the side effects of `login`:
     * keeps the currently selected company (a super/owner may have switched
     * companies mid-session; `login` would reset `companyId` to the user's
     * default and bypass the per-company query-cache invalidation in
     * `setCompanyId`) and does NOT clear the intentional-logout marker (only an
     * interactive login re-enables silent re-auth).
     */
    tokenRefreshed: (state, action: PayloadAction<AuthResponse>) => {
      const user = normalizeSessionUser(action.payload.user);
      state.accessToken = action.payload.accessToken;
      state.user = user;
    },
    logout: (state) => {
      state.user = undefined;
      state.companyId = undefined;
      state.accessToken = "";
      // Abandon any open viewed-as session: the effective-token selector falls
      // back to `impersonation.token`, so leaving it set would keep a live
      // bearer addressable after sign-out.
      state.impersonation = undefined;
    },
    setCompanyId: (state, action: PayloadAction<string>) => {
      state.companyId = action.payload;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = normalizeSessionUser(action.payload);
    },
    /**
     * Enter a viewed-as session. Snapshots the staff identity so exiting
     * restores it, and clears `user` so the shell re-resolves it as the
     * subject — the point of the feature is that everything downstream sees
     * the subject's real role and reach.
     */
    startImpersonation: (
      state,
      action: PayloadAction<
        Omit<ImpersonationSession, "staffUser" | "staffCompanyId">
      >,
    ) => {
      state.impersonation = {
        ...action.payload,
        staffUser: state.user,
        staffCompanyId: state.companyId,
      };
      state.user = undefined;
    },
    /** Leave a viewed-as session and restore the staff identity. */
    stopImpersonation: (state) => {
      const snapshot = state.impersonation;
      state.impersonation = undefined;
      if (snapshot?.staffUser) {
        state.user = snapshot.staffUser;
        state.companyId = snapshot.staffCompanyId;
      }
    },
  },
});

const impersonation = (state: RootState) => state.session.impersonation;

/**
 * The product principal is the viewed-as subject while impersonation remains
 * open. A staff credential refresh can temporarily project the staff user into
 * Redux without replacing that principal.
 */
export const effectiveSessionUserId = (session: SessionState): string | null =>
  session.impersonation?.subjectId ?? session.user?.id ?? null;

/** The bearer ordinary app requests must carry for the effective principal. */
export const effectiveSessionAccessToken = (session: SessionState): string =>
  session.impersonation?.token ?? session.accessToken;

/** The signed-in staff token. Session management only — never app requests. */
const staffAccessToken = (state: RootState) => state.session.accessToken;

/**
 * The token ordinary app requests must carry.
 *
 * While a session is open this is the impersonation bearer, so every existing
 * fetcher (which takes `useSession().accessToken` and hands it to
 * `createAuthenticatedClient`) views the product as the subject with no
 * per-hook change. Mixing the two would make the banner lie about which
 * identity acted, so session-management calls take `staffAccessToken`
 * explicitly.
 */
const accessToken = (state: RootState): string =>
  effectiveSessionAccessToken(state.session);

const companyId = (state: RootState) => state.session.companyId;

const user = (state: RootState) => state.session.user;

const userHasAccess = createSelector(
  [user],
  (user) => (item: Resource) =>
    item.roles ? item.roles.has(user?.role as Role) : true,
);

const userHasRole = createSelector(
  [user],
  (user) =>
    (...roles: Role[]) =>
      roles.includes(user?.role || "member"),
);

// Exported for unit tests: asserts the RBAC decision is resolved from the same
// persisted source as authLoader, so the shell and loader can't diverge.
export const userIsAuthorized = createSelector(
  [user, companyId],
  (user, reduxCompanyId) => (rawPathname: string) => {
    // Under bare-root company routing the shell passes the live slug-prefixed
    // pathname (`/acme/settings`); `authPathForCompanyUrl` strips the deploy
    // base + slug so it matches the unprefixed routeConfig entries.
    // Copilot/literal callers pass already-unprefixed paths, which pass through
    // unchanged. Without this every prefixed route would fail-close below.
    const pathname = authPathForCompanyUrl(rawPathname);

    // Public routes are always accessible
    if (isPublicRoute(pathname)) {
      return true;
    }

    // Use findMatchingRoute for longest-path-first matching
    // This correctly matches /company/hours before /company
    const route = findMatchingRoute(pathname);

    // FAIL-CLOSED: Unknown routes are denied by default
    // This prevents unauthorized access to undefined routes
    if (!route) {
      return false;
    }

    // Check company access for company-required routes
    // This prevents users from seeing content for companies they don't have access to
    if (route.access === "company-required") {
      // Read persisted selection for consistency with authLoader in multi-tab scenarios
      // Without this, Tab A switching companies would cause Tab B's loader to allow
      // (reads new localStorage) but selector to deny (reads stale Redux)
      const currentCompanyId =
        readPersistedCompanyId() ?? reduxCompanyId ?? null;

      // No company selected = deny access (authLoader redirects superusers to /super)
      if (!currentCompanyId) {
        return false;
      }

      // Superusers and owners can access any company once one is selected
      // (owners are limited by their account's companies in the company selector,
      // and the backend validates against account.company_ids)
      // Other roles must have the company in their companyIds array
      const canAccessAnyCompany =
        user?.role === "super" || user?.role === "owner";
      if (
        !canAccessAnyCompany &&
        !user?.companyIds?.includes(currentCompanyId)
      ) {
        return false;
      }
    }

    // RBAC decision (role + permission-derived allowlist): resolve the user from
    // localStorage via the SHARED `currentUser()` — the one gating source used by
    // authLoader, the coach nav, `<Can>`, and `CoachIndex` — so none can reach an
    // opposite decision for the same identity (and role + permissions always come
    // from one blob). Mirrors the localStorage read used above for `companyId`.
    // Falls back to the Redux `user` during SSR, where localStorage is empty.
    const rbacUser = currentUser() ?? user;
    return canAccessRoute(route, rbacUser?.role, permissionsForUser(rbacUser));
  },
);

const userSubRoles = createSelector([userHasRole], (userHasRole) =>
  userHasRole("super", "owner")
    ? ["owner", "admin", "member"]
    : userHasRole("admin")
      ? ["member"]
      : [],
);

export const { reducer } = sessionSlice;

/**
 * React Query cache hygiene for switching the active company: fire the
 * `company-switching` event (lets the UI reset synchronously before queries
 * return), cancel + remove the previous tenant's cached queries so nothing
 * leaks across tenants, and invalidate the next tenant's so any stale data from
 * an earlier session refetches. No-op when the company isn't actually changing.
 *
 * Shared by the `useSession` setter AND the `/:slug` router loader
 * (`companySlugLoader`) so a company switch through either path does the exact
 * same cleanup — a URL-driven switch can't skip the invalidation a UI-driven one
 * performs. Takes the `queryClient` explicitly so the loader (outside React) can
 * call it with the module-level client.
 */
const invalidateCompanyQueriesOnSwitch = (
  queryClient: QueryClient,
  previousCompanyId: string | undefined,
  nextCompanyId: string,
): void => {
  if (previousCompanyId === nextCompanyId) return;

  // Dispatch BEFORE state changes so the UI resets synchronously — prevents a
  // race where the new tenant's queries return before the UI state resets.
  window.dispatchEvent(new CustomEvent("company-switching"));

  if (previousCompanyId) {
    // Cancel in-flight + drop all cached data for the old company so no stale
    // data leaks between tenants.
    queryClient.cancelQueries({
      queryKey: queryKeys.company(previousCompanyId).all,
    });
    queryClient.removeQueries({
      queryKey: queryKeys.company(previousCompanyId).all,
    });
  }

  // Invalidate the new company's queries to force a fresh fetch (in case we hold
  // stale data from a previous session).
  queryClient.invalidateQueries({
    queryKey: queryKeys.company(nextCompanyId).all,
  });
};

export const useSession = () => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const currentCompanyId = useAppSelector(companyId);

  const setCompanyIdWithInvalidation = useCallback(
    (id: string) => {
      invalidateCompanyQueriesOnSwitch(queryClient, currentCompanyId, id);
      dispatch(sessionSlice.actions.setCompanyId(id));
    },
    [dispatch, queryClient, currentCompanyId],
  );

  return {
    accessToken: useAppSelector(accessToken),
    staffAccessToken: useAppSelector(staffAccessToken),
    impersonation: useAppSelector(impersonation),
    companyId: useAppSelector(companyId),
    startImpersonation: useCallback(
      (session: Omit<ImpersonationSession, "staffUser" | "staffCompanyId">) => {
        // Drop every cached response before the identity changes: the cache is
        // keyed by company and query, not by viewer, so a leftover entry would
        // render the staff user's data inside the viewed-as surface.
        queryClient.removeQueries();
        // Record the bearer so the 401 recovery path can recognise — and
        // refuse to replay — an impersonated request as the staff user, including
        // one whose response lands after the actor has exited.
        rememberImpersonationToken(session.token);
        dispatch(sessionSlice.actions.startImpersonation(session));
      },
      [dispatch, queryClient],
    ),
    stopImpersonation: useCallback(() => {
      queryClient.removeQueries();
      dispatch(sessionSlice.actions.stopImpersonation());
    }, [dispatch, queryClient]),
    hydrate: useCallback(
      () => dispatch(sessionSlice.actions.hydrate(readPersistedSession(true))),
      [dispatch],
    ),
    login: useCallback(
      (data: AuthResponse) => dispatch(sessionSlice.actions.login(data)),
      [dispatch],
    ),
    logout: useCallback(() => {
      // Read straight from storage: the in-memory registration is gone after a
      // cold start, and this is the last moment the token is still valid.
      const accessToken = readPersistedAccessToken();
      const pushCompanyId = readPersistedCompanyId();
      void revokeRegisteredDispatchPushDevice(
        accessToken && pushCompanyId
          ? { apiUrl: getApiUrl(), accessToken, companyId: pushCompanyId }
          : undefined,
      );
      // SD-16321: stop the native CallKit layer answering VoIP pushes for the
      // signed-out user (no-op on web; never rejects).
      void teardownNativeCallKit();
      // Mark the logout as intentional FIRST (synchronously). This marker is what
      // actually guarantees the logout sticks: silent re-auth honors it and won't
      // resurrect the session even if the Firebase signOut below fails (worst on
      // shared devices). The expired-token path in authLoader dispatches the raw
      // `logout` action WITHOUT this marker, so silent re-auth still recovers
      // there.
      markIntentionalLogout();
      // Clear the local session synchronously so a caller that navigates to
      // /login in the same tick can't have the login page observe a still-live
      // session and bounce back into the app.
      dispatch(sessionSlice.actions.logout());
      // End the Firebase session in the background (retry once); the intentional
      // -logout marker covers the case where this ultimately fails. Next login
      // then correctly shows the account chooser.
      if (isFirebaseReady()) {
        const auth = getFirebaseAuth();
        signOut(auth)
          .catch(() => signOut(auth))
          .catch((error) =>
            safeConsole.error(
              "authentication-lifecycle",
              "Firebase sign-out failed during logout.",
              error,
            ),
          );
      }
    }, [dispatch]),
    setCompanyId: setCompanyIdWithInvalidation,
    setUser: useCallback(
      (user: User) => dispatch(sessionSlice.actions.setUser(user)),
      [dispatch],
    ),
    user: useAppSelector(user),
    userHasAccess: useAppSelector(userHasAccess),
    userHasRole: useAppSelector(userHasRole),
    userIsAuthorized: useAppSelector(userIsAuthorized),
    userSubRoles: useAppSelector(userSubRoles),
  };
};

// Hook to handle session hydration on app startup
export const useHydrateSession = () => {
  const { hydrate } = useSession();

  useEffect(() => {
    // Hydrate session from localStorage on client side
    hydrate();
  }, [hydrate]);
};

/**
 * Keep this tab's Redux session in sync with auth changes made by OTHER tabs.
 * Requests read their Bearer token from Redux, but a silent re-mint (or a
 * logout) in another tab only writes localStorage — without this listener the
 * other tabs keep sending the stale token until reload. `storage` events fire
 * only in non-originating tabs, so this never double-processes local changes.
 * Company selection is deliberately NOT synced here: cross-tab company reads
 * already go through localStorage where isolation matters (see
 * userIsAuthorized), and force-switching a visible tab's company would be a
 * worse surprise than the pre-existing behavior.
 */
export const useCrossTabSessionSync = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      // key === null means localStorage.clear() — resync everything.
      if (isAuthSyncStorageKey(event.key)) {
        dispatch(sessionSlice.actions.syncAuthFromStorage(readPersistedAuth()));
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [dispatch]);
};
